import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import {
  captureEmailSignInPrompt,
  clearCapturedEmailSignInPrompt,
  isEmailSignInPrompt,
  stripEmailSignInPrompt,
} from '../utils/emailSignInPrompt';

interface UseEmailSignInPromptOptions {
  /** Called when the user must sign in (opens login modal). */
  onPrompt: () => void;
  /** Called when already signed in after landing from an email link. */
  onAlreadySignedIn?: () => void;
}

/**
 * When the page is opened via an email link with ?signin=1, prompt login if needed.
 * Strips the query param from the URL after handling.
 */
export function useEmailSignInPrompt(options: UseEmailSignInPromptOptions): void {
  const { onPrompt, onAlreadySignedIn } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const handledRef = useRef(false);
  const signInRequestedRef = useRef(captureEmailSignInPrompt());

  useEffect(() => {
    if (!authReady || handledRef.current || !signInRequestedRef.current) {
      return;
    }

    handledRef.current = true;
    if (isEmailSignInPrompt(searchParams)) {
      const cleaned = stripEmailSignInPrompt(searchParams);
      setSearchParams(cleaned, { replace: true });
    }

    if (!isAuthenticated) {
      onPrompt();
      return;
    }

    clearCapturedEmailSignInPrompt();
    onAlreadySignedIn?.();
  }, [authReady, isAuthenticated, searchParams, setSearchParams, onPrompt, onAlreadySignedIn]);
}
